import axios from "axios";
import { header } from './header.encry';

const API_URL = process.env.REACT_APP_PEGASUS_API_URL;

// Cache for WooCommerce platform credentials
let wooCommercePlatformCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const fetchWooCommercePlatform = async () => {
  // Return cached data if still valid
  if (wooCommercePlatformCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
    return wooCommercePlatformCache;
  }

  try {
    const response = await axios.get(`${API_URL}/plateforms`, header());
    const platforms = response.data;
    
    // Find the WooCommerce platform (accept 'woocommerce' or short 'woo')
    const wooCommercePlatform = platforms.find(p => {
      const t = (p.type || '').toLowerCase();
      return t === 'woocommerce' || t === 'woo';
    });

    if (!wooCommercePlatform) {
      console.warn('No WooCommerce platform found in database');
      return null;
    }

    wooCommercePlatformCache = wooCommercePlatform;
    cacheTimestamp = Date.now();
    return wooCommercePlatform;
  } catch (error) {
    console.error('Error fetching WooCommerce platform:', error);
    return null;
  }
};

const getWooHeaders = async () => {
  const platform = await fetchWooCommercePlatform();
  if (!platform) {
    console.error('WooCommerce platform not found');
    throw new Error('WooCommerce platform not configured');
  }

  // Prefer credentials stored inside additionnal_data.data
  const data = platform.additionnal_data && platform.additionnal_data.data ? platform.additionnal_data.data : platform.additionnal_data || {};

  // consumer_key/consumer_secret (Woo REST API standard)
  if (data.consumer_key && data.consumer_secret) {
    const credentialSource = 'additionnal_data.data.consumer_key/consumer_secret';
    console.debug('WooCommerce: using credentials from', credentialSource);
    return {
      "Content-Type": "application/json",
      Authorization: "Basic " + btoa(`${data.consumer_key}:${data.consumer_secret}`),
    };
  }

  // api_key or token (use Bearer)
  if (data.api_key || data.token || data.jwtToken) {
    const token = data.api_key || data.token || data.jwtToken;
    const credentialSource = data.api_key ? 'additionnal_data.data.api_key' : data.token ? 'additionnal_data.data.token' : 'additionnal_data.data.jwtToken';
    console.debug('WooCommerce: using token from', credentialSource);
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }

  // Fallback to legacy login/password on platform root
  if (platform.login && platform.password) {
    console.debug('WooCommerce: using credentials from platform.login/platform.password');
    return {
      "Content-Type": "application/json",
      Authorization: "Basic " + btoa(`${platform.login}:${platform.password}`),
    };
  }

  console.error('WooCommerce credentials not found in platform (checked additionnal_data.data and login/password)');
  throw new Error('WooCommerce credentials not configured');
};

const getWooApiUrl = async () => {
  const platform = await fetchWooCommercePlatform();
  if (!platform) {
    console.error('WooCommerce platform not found');
    throw new Error('WooCommerce platform not configured');
  }

  // Try multiple possible locations for the API URL
  const data = platform.additionnal_data && platform.additionnal_data.data ? platform.additionnal_data.data : platform.additionnal_data || {};
  const candidates = [data.url, data.api_url, platform.additionnal_data?.url, platform.additionnal_data?.api_url, platform.url, platform.host].filter(Boolean);
  const apiUrl = candidates.length ? candidates[0] : null;

  if (!apiUrl) {
    console.error('WooCommerce URL not found in platform (checked multiple fields)');
    throw new Error('WooCommerce URL not configured');
  }

  return apiUrl;
};

const getWooRequestConfig = async () => {
  const platform = await fetchWooCommercePlatform();
  if (!platform) {
    console.error('WooCommerce platform not found');
    throw new Error('WooCommerce platform not configured');
  }

  const data = platform.additionnal_data && platform.additionnal_data.data ? platform.additionnal_data.data : platform.additionnal_data || {};

  // Determine headers and auth params
  let headers = { 'Content-Type': 'application/json' };
  let authParams = {};

  if (data.consumer_key && data.consumer_secret) {
    // Use query params for consumer key/secret (works on many WooCommerce installs)
    authParams.consumer_key = data.consumer_key;
    authParams.consumer_secret = data.consumer_secret;
  } else if (data.api_key || data.token || data.jwtToken) {
    const token = data.api_key || data.token || data.jwtToken;
    headers.Authorization = `Bearer ${token}`;
  } else if (platform.login && platform.password) {
    headers.Authorization = 'Basic ' + btoa(`${platform.login}:${platform.password}`);
  }

  const apiUrl = await getWooApiUrl();
  return { apiUrl, headers, authParams };
};

const decodeWooHtml = (value = '') => {
  return new DOMParser().parseFromString(String(value), 'text/html').body.textContent || '';
};

const fetchWooCommerceCategoriesRaw = async () => {
  const { apiUrl, headers, authParams } = await getWooRequestConfig();
  const allCategories = [];
  let currentPage = 1;

  while (true) {
    const response = await axios.get(`${apiUrl}/wp-json/wc/v3/products/categories`, {
      headers,
      params: { per_page: 100, page: currentPage, ...authParams },
    });

    const pageData = Array.isArray(response.data) ? response.data : [];
    allCategories.push(...pageData);

    const totalPages = Number(response.headers['x-wp-totalpages'] || 1);
    if (currentPage >= totalPages || pageData.length < 100) {
      break;
    }

    currentPage += 1;
  }

  return allCategories;
};

export const getCategoriesWooCommerce = async () => {
  try {
    const allCategories = await fetchWooCommerceCategoriesRaw();
    if (!allCategories.length) {
      console.warn("Empty response from WooCommerce API");
      return [];
    }

    return allCategories
      .map((x) => ({
        value: x.id,
        label: decodeWooHtml(x.name),
        parent: x.parent,
      }))
      .sort((left, right) => {
        const leftParent = Number(left.parent || 0);
        const rightParent = Number(right.parent || 0);
        if (leftParent !== rightParent) {
          return leftParent - rightParent;
        }
        return String(left.label || '').localeCompare(String(right.label || ''));
      });
  } catch (error) {
    console.error("Error fetching WooCommerce categories:", error);
    return []; // Return empty array in case of an error
  }
};

export const getWooCommerceCategoriesDetailed = async () => {
  try {
    const allCategories = await fetchWooCommerceCategoriesRaw();
    return allCategories.map((category) => ({
      id: Number(category.id),
      name: decodeWooHtml(category.name),
      slug: category.slug || '',
      description: decodeWooHtml(category.description || ''),
      parent: Number(category.parent || 0),
      count: Number(category.count || 0),
      image: category.image?.src || '',
      menu_order: Number(category.menu_order || 0),
    }));
  } catch (error) {
    console.error('Error fetching WooCommerce categories details:', error);
    throw error;
  }
};

const buildWooCategoryPayload = (data = {}) => {
  const payload = {};

  if (typeof data.name === 'string' && data.name.trim()) {
    payload.name = data.name.trim();
  }

  if (typeof data.slug === 'string' && data.slug.trim()) {
    payload.slug = data.slug.trim();
  }

  if (typeof data.description === 'string') {
    payload.description = data.description.trim();
  }

  if (Object.prototype.hasOwnProperty.call(data, 'parent')) {
    payload.parent = Number(data.parent) || 0;
  }

  if (Object.prototype.hasOwnProperty.call(data, 'image')) {
    if (data.image === null || data.image === '') {
      payload.image = null;
    } else if (typeof data.image === 'string' && data.image.trim()) {
      payload.image = { src: data.image.trim() };
    }
  }

  return payload;
};

export const createWooCommerceCategory = async (data = {}) => {
  try {
    const { apiUrl, headers, authParams } = await getWooRequestConfig();
    const payload = buildWooCategoryPayload(data);
    const response = await axios.post(
      `${apiUrl}/wp-json/wc/v3/products/categories`,
      payload,
      { headers, params: authParams }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating WooCommerce category:', error);
    throw error;
  }
};

export const updateWooCommerceCategory = async (categoryId, data = {}) => {
  try {
    const { apiUrl, headers, authParams } = await getWooRequestConfig();
    const payload = buildWooCategoryPayload(data);
    const response = await axios.put(
      `${apiUrl}/wp-json/wc/v3/products/categories/${categoryId}`,
      payload,
      { headers, params: authParams }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating WooCommerce category:', error);
    throw error;
  }
};

export const deleteWooCommerceCategory = async (categoryId) => {
  try {
    const { apiUrl, headers, authParams } = await getWooRequestConfig();
    const response = await axios.delete(
      `${apiUrl}/wp-json/wc/v3/products/categories/${categoryId}`,
      { headers, params: { ...authParams, force: true } }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting WooCommerce category:', error);
    throw error;
  }
};

export const getWooCommerceProductsPage = async ({ per_page = 50, page = 1, status, type, search } = {}) => {
  try {
    const { apiUrl, headers, authParams } = await getWooRequestConfig();
    const params = {
      per_page,
      page,
      ...authParams,
    };

    if (status) {
      params.status = status;
    }

    if (type) {
      params.type = type;
    }

    if (search) {
      params.search = search;
    }

    const response = await axios.get(`${apiUrl}/wp-json/wc/v3/products`, {
      headers,
      params,
    });

    return {
      data: Array.isArray(response.data) ? response.data : [],
      total: Number(response.headers['x-wp-total'] || 0),
      totalPages: Number(response.headers['x-wp-totalpages'] || 1),
      page,
      perPage: per_page,
    };
  } catch (error) {
    console.error("Error fetching WooCommerce products page:", error);
    throw error;
  }
};

export const searchWooCommerceProducts = async ({ search = '', per_page = 20, page = 1, status = 'publish' } = {}) => {
  const response = await getWooCommerceProductsPage({
    per_page,
    page,
    status,
    search,
  });

  return {
    data: Array.isArray(response?.data) ? response.data : [],
    total: Number(response?.total || 0),
    totalPages: Number(response?.totalPages || 1),
    page,
    perPage: per_page,
  };
};

export const createWooCommerceProduct = async (objectWoo) => {
  try{
        const images = Array.isArray(objectWoo.images) && objectWoo.images.length > 0
          ? objectWoo.images.filter(Boolean).map((src) => ({ src }))
          : objectWoo.image
            ? [{ src: objectWoo.image }]
            : [];

        const fullDescription = String(
          objectWoo.long_description || objectWoo.description || ''
        ).trim();
        const shortDescription = String(
          objectWoo.short_description || objectWoo.description || ''
        ).trim();

        const data = {
            name: objectWoo.name,
            type: 'simple',
            regular_price: String(objectWoo.regular_price)  ,
            description: fullDescription,
            ...(shortDescription ? { short_description: shortDescription } : {}),
            categories: objectWoo.categories,
            images,
            ...(objectWoo.manage_stock !== undefined ? { manage_stock: Boolean(objectWoo.manage_stock) } : {}),
            ...(objectWoo.stock_quantity !== undefined ? { stock_quantity: Number(objectWoo.stock_quantity) || 0 } : {}),
            ...(objectWoo.stock_status ? { stock_status: objectWoo.stock_status } : {}),
            ...(Array.isArray(objectWoo.meta_data) ? { meta_data: objectWoo.meta_data } : {}),
            sku:"CLDMA"+objectWoo.barcode,
                    
           
          };
      
          const { apiUrl, headers, authParams } = await getWooRequestConfig();

         const response = await axios.post(`${apiUrl}/wp-json/wc/v3/products`, data, { headers, params: authParams });

         if(response.status==201){
            const resupdate = await axios.put(`${apiUrl}/wp-json/wc/v3/products/${response.data.id}`,{
            meta_data: [
                  {
                    key: "rank_math_focus_keyword",
                    value: objectWoo.name
                  },
                  {
                    key: "ean",
                    value: objectWoo.barcode
                  },
                  {
                    key: "gtin",
                    value: objectWoo.barcode
                  },
                  {
                    key: "_alg_ean",
                    value: objectWoo.barcode
                  },
                  
                ]  ,
                ean:objectWoo.barcode
              },{ headers, params: authParams });

         }
         return response;
    }catch(ex){
        return {status:500};
    }


  };

  export const updateWooCommerceProductCategories = async (productId, categories, extraFields = {}) => {
    try {
        const { apiUrl, headers, authParams } = await getWooRequestConfig();
        const payload = {
          categories,
          ...(extraFields && typeof extraFields === 'object' ? extraFields : {}),
        };
        return await axios.put(
          `${apiUrl}/wp-json/wc/v3/products/${productId}`,
          payload,
          { headers, params: authParams }
        );
    } catch (error) {
      console.error("Error updating WooCommerce product categories:", error);
      throw error;
    }
  };

  export const getWooCommerceProduct = async (productId) => {
    try {
      const { apiUrl, headers, authParams } = await getWooRequestConfig();
      const response = await axios.get(`${apiUrl}/wp-json/wc/v3/products/${productId}`, { headers, params: authParams });
      return response.data;
    } catch (error) {
      console.error("Error fetching WooCommerce product:", error);
      throw error;
    }
  };

  export const getWooCommerceProductsByIds = async (productIds = []) => {
    try {
      const ids = Array.from(new Set((Array.isArray(productIds) ? productIds : []).filter(Boolean)));
      if (!ids.length) {
        return [];
      }

      const { apiUrl, headers, authParams } = await getWooRequestConfig();
      const response = await axios.get(`${apiUrl}/wp-json/wc/v3/products`, {
        headers,
        params: {
          include: ids.join(','),
          per_page: Math.min(Math.max(ids.length, 1), 100),
          ...authParams,
        },
      });

      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error("Error fetching WooCommerce products by ids:", error);
      throw error;
    }
  };

  export const updateWooCommerceProductStockStatus = async (productId, inStock) => {
    try {
      const { apiUrl, headers, authParams } = await getWooRequestConfig();
      const response = await axios.put(
        `${apiUrl}/wp-json/wc/v3/products/${productId}`,
        {
          manage_stock: false,
          stock_status: inStock ? 'instock' : 'outofstock',
        },
        { headers, params: authParams }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating WooCommerce product stock status:", error);
      throw error;
    }
  };

  export const updateWooCommerceProductFeaturedStatus = async (productId, featured) => {
    try {
      const { apiUrl, headers, authParams } = await getWooRequestConfig();
      const response = await axios.put(
        `${apiUrl}/wp-json/wc/v3/products/${productId}`,
        {
          featured: Boolean(featured),
        },
        { headers, params: authParams }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating WooCommerce product featured status:", error);
      throw error;
    }
  };

  export const updateWooCommerceProductPricing = async (productId, pricing = {}) => {
    try {
      const { apiUrl, headers, authParams } = await getWooRequestConfig();
      const payload = {};

      if (Object.prototype.hasOwnProperty.call(pricing, 'regular_price')) {
        payload.regular_price = pricing.regular_price === '' || pricing.regular_price === null || pricing.regular_price === undefined
          ? ''
          : String(pricing.regular_price);
      }

      if (Object.prototype.hasOwnProperty.call(pricing, 'sale_price')) {
        payload.sale_price = pricing.sale_price === '' || pricing.sale_price === null || pricing.sale_price === undefined
          ? ''
          : String(pricing.sale_price);
      }

      const response = await axios.put(
        `${apiUrl}/wp-json/wc/v3/products/${productId}`,
        payload,
        { headers, params: authParams }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating WooCommerce product pricing:", error);
      throw error;
    }
  };

  export const updateWooCommerceProductVaseMeta = async (productId, vaseInclusion = 'unspecified') => {
    if (!['unspecified', 'included', 'not_included'].includes(vaseInclusion)) {
      throw new Error('Invalid vase inclusion');
    }
    const { apiUrl, headers, authParams } = await getWooRequestConfig();
    const response = await axios.put(
      `${apiUrl}/wp-json/wc/v3/products/${productId}`,
      { meta_data: [{ key: '_pegasus_vase_inclusion', value: vaseInclusion }] },
      { headers, params: authParams }
    );
    return response.data;
  };

  export const updateWooCommerceProductDeliveryMeta = async (productId, deliveryConfig = {}) => {
    try {
      const { apiUrl, headers, authParams } = await getWooRequestConfig();
      const expressZones = Array.isArray(deliveryConfig.express_zones)
        ? deliveryConfig.express_zones
            .filter((zone) => zone && (zone.code || zone.value))
            .map((zone) => ({
              code: String(zone.code || zone.value || '').trim().toUpperCase(),
              name: String(zone.name || zone.label || '').trim(),
            }))
        : [];

      const payload = {
        meta_data: [
          {
            key: '_pegasus_delivery_express_enabled',
            value: deliveryConfig.express_enabled ? '1' : '0',
          },
          {
            key: '_pegasus_delivery_express_zones',
            value: JSON.stringify(expressZones),
          },
          {
            key: '_pegasus_delivery_morocco_enabled',
            value: deliveryConfig.morocco_enabled ? '1' : '0',
          },
        ],
      };

      const response = await axios.put(
        `${apiUrl}/wp-json/wc/v3/products/${productId}`,
        payload,
        { headers, params: authParams }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating WooCommerce product delivery meta:", error);
      throw error;
    }
  };

  export const updateWooCommerceProductAddonsMeta = async (productId, addons = []) => {
    try {
      const { apiUrl, headers, authParams } = await getWooRequestConfig();
      const normalizedAddons = Array.isArray(addons)
        ? addons
            .filter((addon) => addon && (addon._id || addon.id) && addon.name)
            .map((addon) => ({
              id: String(addon._id || addon.id),
              name: String(addon.name || ''),
              type: String(addon.input_type || addon.type || 'select'),
              required: Boolean(addon.required),
              multiple: Boolean(addon.multiple),
              options: Array.isArray(addon.options)
                ? addon.options
                    .filter((option) => option && (option._id || option.id) && option.name)
                    .map((option) => ({
                      id: String(option._id || option.id),
                      name: String(option.name || ''),
                      price: Number(option.price || 0),
                      price_delta: Number(option.price_delta ?? option.price ?? 0),
                      sale_price_delta: Number(option.sale_price_delta || 0),
                      image_url: String(option.image_url || option.imageUrl || ''),
                      color: String(option.color || ''),
                    }))
                : [],
            }))
        : [];

      const response = await axios.put(
        `${apiUrl}/wp-json/wc/v3/products/${productId}`,
        {
          meta_data: [
            {
              key: '_pegasus_product_addons',
              value: JSON.stringify(normalizedAddons),
            },
          ],
        },
        { headers, params: authParams }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating WooCommerce product addons meta:", error);
      throw error;
    }
  };

  /**
   * Fetch orders from WooCommerce REST API using credentials from .env
   * @param {Object} opts
   * @param {Array<string>} opts.statuses - array of status strings (e.g. ['processing','on-hold'])
   * @param {number} opts.per_page - items per page (max 100)
   * @param {number} opts.page - page number (1-based)
   * @returns {Promise<Object[]>} response data array
   */
  export const getWooCommerceOrders = async ({ statuses = [], per_page = 50, page = 1 } = {}) => {
    try {
      const headers = await getWooHeaders();
      const apiUrl = await getWooApiUrl();
      const params = {
        per_page,
      };

      // Allowed statuses supported by this WooCommerce instance (based on API error list)
      const allowedStatuses = [
        'any', 'trash', 'auto-draft', 'en-cours-mp', 'pending', 'delivery-problem', 'not-available', 'waiting-delivery', 'pickup',
        'en-cours-de-produ', 'en-cours-prod-mp', 'delivery-inroute', 'failed', 'refunded', 'cancelled', 'completed', 'on-hold',
        'accepte', 'capture', 'processing', 'refuse', 'refused-by', 'checkout-draft'
      ];

      if (Array.isArray(statuses) && statuses.length > 0) {
        const normalized = statuses.map(s => String(s || '').trim()).filter(Boolean);
        const filtered = normalized.filter(s => allowedStatuses.includes(s));
        const invalid = normalized.filter(s => !allowedStatuses.includes(s));
        if (invalid.length > 0) {
          console.warn('WooCommerce: removed invalid statuses:', invalid);
        }
        if (filtered.length > 0) {
          params.status = filtered.join(',');
        }
      }

      const url = `${apiUrl}/wp-json/wc/v3/orders`;

      // Accumulate pages when the returned page size equals the requested `per_page`.
      const allData = [];
      let currentPage = Number(page) || 1;
      let total = 0;
      let totalPages = 1;

      while (true) {
        params.page = currentPage;
        const { apiUrl, headers, authParams } = await getWooRequestConfig();
        // merge auth params with per-request params
        const response = await axios.get(url, { headers, params: { ...params, ...authParams } });
        const pageData = Array.isArray(response.data) ? response.data : [];

        // Clean pageData by removing unwanted meta entries
        const cleaned = pageData.map(order => {
          const o = { ...order };
          if (Array.isArray(o.meta_data)) {
            o.meta_data = o.meta_data.filter(m => !(m && String(m.key) === '_ywpar_total_points'));
          }
          if (Array.isArray(o.line_items)) {
            o.line_items = o.line_items.map(li => ({
              ...li,
              meta_data: Array.isArray(li.meta_data) ? li.meta_data.filter(m => !(m && String(m.key) === '_ywpar_total_points')) : li.meta_data
            }));
          }
          return o;
        });

        allData.push(...cleaned);

        total = Number(response.headers['x-wp-total'] || allData.length);
        totalPages = Number(response.headers['x-wp-totalpages'] || totalPages);

        // If this page returned fewer items than requested, we're done.
        if (pageData.length < per_page) break;

        // Otherwise, if we reached the last page by header, stop.
        if (currentPage >= totalPages) break;

        // Otherwise increment page and continue fetching.
        currentPage += 1;
      }

      return { data: allData, total, totalPages };
    } catch (error) {
      console.error('Error fetching WooCommerce orders:', error);
      return { data: [], total: 0, totalPages: 0 };
    }
  };

  export const updateWooOrder = async (orderId, payload) => {
    try {
      const headers = await getWooHeaders();
      const apiUrl = await getWooApiUrl();
      const url = `${apiUrl}/wp-json/wc/v3/orders/${orderId}`;
      const response = await axios.put(url, payload, { headers });
      return response.data;
    } catch (error) {
      console.error('Error updating WooCommerce order:', error);
      throw error;
    }
  };

  export const createWooCommerceOrder = async (payload) => {
    try {
      const { apiUrl, headers, authParams } = await getWooRequestConfig();
      const response = await axios.post(
        `${apiUrl}/wp-json/wc/v3/orders`,
        payload,
        { headers, params: authParams }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating WooCommerce order:', error);
      throw error;
    }
  };

